import type React from 'react'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { AthleteSummary } from '../types/athleteSummary'
import { useAuth } from './AuthContext'

interface AthletesContextType {
  athletes: AthleteSummary[]
  loading: boolean
  error: string | null
  refreshAthletes: () => Promise<void>
  addSelfAsAthlete: () => Promise<{ success: boolean; error?: string }>
  removeAthlete: (athleteId: string) => Promise<{ success: boolean; error?: string }>
}

const AthletesContext = createContext<AthletesContextType | undefined>(undefined)

export const useAthletes = () => {
  const context = useContext(AthletesContext)
  if (context === undefined) {
    throw new Error('useAthletes must be used within an AthletesProvider')
  }
  return context
}

interface AthletesProviderProps {
  children: React.ReactNode
}

export const AthletesProvider: React.FC<AthletesProviderProps> = ({ children }) => {
  const { user } = useAuth()
  const [athletes, setAthletes] = useState<AthleteSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refreshAthletes = useCallback(async () => {
    if (!user) {
      setAthletes([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Fetch athletes connected to this coach using the helper function
      const { data, error: rpcError } = await supabase.rpc('get_coach_athletes', {
        target_coach_id: user.id,
      })

      if (rpcError) {
        console.error('Error fetching coach athletes:', rpcError)
        setError('Failed to load athletes')
        return
      }

      if (!data || data.length === 0) {
        // No athletes connected
        console.log('No athletes connected to coach')
        setAthletes([])
        return
      }

      // Transform real athlete data to match AthleteSummary structure
      const athleteSummaries: AthleteSummary[] = data.map((athlete: any) => {
        const summary: AthleteSummary = {
          id: athlete.athlete_id,
          avatar: athlete.athlete_avatar,
          name:
            `${athlete.athlete_first_name || ''} ${athlete.athlete_last_name || ''}`.trim() || athlete.athlete_email,
          sport: 'Triathlon', // Default sport - can be customized later when profiles table has this field
          weeklyStats: {
            hoursCompleted: Number(athlete.weekly_hours_completed) || 0,
            hoursPlanned: Number(athlete.weekly_hours_planned) || 0,
            workoutsCompleted: Number(athlete.weekly_workouts_completed) || 0,
            workoutsPlanned: Number(athlete.weekly_workouts_planned) || 0,
            avgSleepHours: athlete.weekly_avg_sleep_hours ?? null,
          },
          hasDeviceConnections: athlete.has_device_connections || false,
        }

        // Add last activity if available
        if (athlete.last_activity_description && athlete.last_activity_date) {
          summary.lastActivity = {
            description: athlete.last_activity_description,
            activityType: athlete.last_activity_type || '',
            date: athlete.last_activity_date,
            durationSeconds: athlete.last_activity_duration_seconds,
            workoutId: athlete.last_activity_workout_id,
          }
        }

        return summary
      })

      setAthletes(athleteSummaries)
    } catch (err) {
      console.error('Exception fetching coach athletes:', err)
      setError('Failed to load athletes')
    } finally {
      setLoading(false)
    }
  }, [user])

  const addSelfAsAthlete = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!user?.id) {
      return { success: false, error: 'User not authenticated' }
    }

    try {
      // Create the athlete-to-coach connection where coach adds themselves
      const { error: connectionError } = await supabase.from('athlete_to_coach').insert({
        athlete_id: user.id,
        coach_id: user.id,
      })

      if (connectionError) {
        console.error('Error creating self connection:', connectionError)
        return { success: false, error: 'Failed to add yourself as an athlete. Please try again.' }
      }

      // Add athlete role to the user
      const { error: roleError } = await supabase.rpc('add_user_role', {
        target_role: 'athlete',
      })

      if (roleError) {
        console.error('Error adding athlete role:', roleError)
        // Don't fail if role addition fails, connection was created
      }

      // Refresh the athletes list
      await refreshAthletes()

      return { success: true }
    } catch (err) {
      console.error('Error adding self as athlete:', err)
      return { success: false, error: 'An unexpected error occurred. Please try again.' }
    }
  }, [user, refreshAthletes])

  const removeAthlete = useCallback(
    async (athleteId: string): Promise<{ success: boolean; error?: string }> => {
      if (!user?.id) {
        return { success: false, error: 'User not authenticated' }
      }

      try {
        // Remove the athlete-to-coach connection
        const { error: deleteError } = await supabase
          .from('athlete_to_coach')
          .delete()
          .eq('athlete_id', athleteId)
          .eq('coach_id', user.id)

        if (deleteError) {
          console.error('Error removing athlete connection:', deleteError)
          return { success: false, error: 'Failed to remove athlete. Please try again.' }
        }

        // Refresh the athletes list
        await refreshAthletes()

        return { success: true }
      } catch (err) {
        console.error('Error removing athlete:', err)
        return { success: false, error: 'An unexpected error occurred. Please try again.' }
      }
    },
    [user, refreshAthletes]
  )

  useEffect(() => {
    refreshAthletes()
  }, [refreshAthletes])

  const value: AthletesContextType = {
    athletes,
    loading,
    error,
    refreshAthletes,
    addSelfAsAthlete,
    removeAthlete,
  }

  return <AthletesContext.Provider value={value}>{children}</AthletesContext.Provider>
}
